import * as Sentry from "@sentry/react";
import config from "@utils/config";
import { shouldSendRemoteTelemetry } from "@utils/envs";
import { getSuppressedNoiseEventBreadcrumb } from "@utils/sentry/eventFilters";
import {
  hasUser,
  identifiedUser,
  isSameUser,
  type SentryIdentity,
} from "@utils/sentry/identity";

export const navigationIntegration = {
  registerNavigationContainer: () => {},
};

export const addBreadcrumb = Sentry.addBreadcrumb;
export const captureSentryException = Sentry.captureException;
export const flush = Sentry.flush;
export const getClient = Sentry.getClient;
export const metrics = Sentry.metrics;
export const sentryLogger = Sentry.logger;
export const setUser = Sentry.setUser;
export function identifyUser(identity: SentryIdentity | null) {
  const known = Sentry.getIsolationScope().getUser();
  if (!identity) {
    if (hasUser(known)) Sentry.setUser(null);
    return;
  }
  const next = identifiedUser(known, identity);
  if (!isSameUser(known, next)) Sentry.setUser(next);
}
export const reportAppLoaded = () => {};
export const wrapExpoRouterErrorBoundary = <T>(ErrorBoundary: T) =>
  ErrorBoundary;

export function wrapRootLayout(RootLayout: any) {
  return Sentry.withProfiler(RootLayout);
}

export function initSentry() {
  if (Sentry.getClient()) return;
  if (!config.SENTRY_DSN) return;
  if (!shouldSendRemoteTelemetry()) return;

  Sentry.init({
    dsn: config.SENTRY_DSN,
    environment: config.SENTRY_ENVIRONMENT,
    release: config.SENTRY_RELEASE || undefined,
    sendDefaultPii: true,
    enableLogs: true,

    // Performance
    tracesSampleRate: 0.1,

    // Session replay
    replaysSessionSampleRate: 1.0,
    replaysOnErrorSampleRate: 1.0,

    // Distributed tracing — connects mobile traces to backend
    tracePropagationTargets: [config.API_URL],

    beforeSend(event) {
      const breadcrumb = getSuppressedNoiseEventBreadcrumb(event);

      if (breadcrumb) {
        Sentry.addBreadcrumb(breadcrumb);
        return null;
      }

      return event;
    },

    integrations: (defaultIntegrations) => [
      ...defaultIntegrations.filter(
        (integration) => integration.name !== "ConsoleLogs",
      ),
      Sentry.replayIntegration({
        maskAllText: true,
        maskAllInputs: true,
      }),
    ],
  });
}
